import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
} from "react-router-dom";
import { useEffect, useState } from "react";
import { ClerkProvider, useAuth, useUser } from "@clerk/clerk-react";
import "./App.css";
import ResultPage from "./pages/resultsPage/resultsPage";
import SearchPage from "./pages/searchPage/searchPage.js";
import Dashboard from "./pages/dashboard/Dashboard";
import NewEntryPage from "./pages/newEntry/newEntry";
import ViewBooks from "./pages/viewBooks/ViewBooks";
import Login from "./pages/login/login";
import SignUp from "./pages/signup/signup";
import UserProfile from "./pages/userProfile/UserProfile";
import Projects from "./pages/projects/Projects";
import Terms from "./pages/terms/Terms";
import EditBook from "./pages/editBook/EditBook";
import { AuthInterceptorProvider } from "./api/AuthInterceptorProvider";
import { ThemeProvider } from "./context/ThemeContext";
import { Toaster } from "react-hot-toast";
import ParticleBackground from "./components/particleBackground/ParticleBackground";

function App() {
  const [searchPhrase, setSearchPhrase] = useState("");

  return (
    <ThemeProvider>
      <div className="min-h-screen bg-mainWhite transition-colors duration-200">
        <ParticleBackground />
        <div className="relative z-10">
        <Toaster
          position="bottom-center"
          toastOptions={{
            duration: 3000,
            style: {
              background: "rgb(var(--color-surface) / 1)",
              color: "rgb(var(--color-foreground) / 1)",
              boxShadow: "var(--shadow-result)",
              borderRadius: "1.25rem",
              maxWidth: "min(380px, calc(100vw - 2rem))",
            },
          }}
        />
        <ClerkProvider
          publishableKey={process.env.REACT_APP_CLERK_PUBLISHABLE_KEY}
          afterSignOutUrl="/"
        >
          <AuthInterceptorProvider>
            <BrowserRouter>
              <Routes>
                <Route
                  path="/"
                  element={<SearchPage setSearchPhrase={setSearchPhrase} />}
                />
                <Route
                  path="/search"
                  element={<ResultPage searchPhrase={searchPhrase} />}
                />
                <Route
                  path="/dashboard"
                  element={
                    <RequireAdmin>
                      <Dashboard />
                    </RequireAdmin>
                  }
                />
                <Route
                  path="/new-book"
                  element={
                    <RequireAdmin>
                      <NewEntryPage />
                    </RequireAdmin>
                  }
                />
                <Route
                  path="/view-books"
                  element={
                    <RequireAdmin>
                      <ViewBooks />
                    </RequireAdmin>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<SignUp />} />
                <Route path="/terms" element={<Terms />} />
                <Route
                  path="/profile"
                  element={
                    <RequireAuth>
                      <UserProfile />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/projects"
                  element={
                    <RequireAuth>
                      <Projects />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/edit-book/:id"
                  element={
                    <RequireAdmin>
                      <EditBook />
                    </RequireAdmin>
                  }
                />
              </Routes>
            </BrowserRouter>
          </AuthInterceptorProvider>
        </ClerkProvider>
        </div>
      </div>
    </ThemeProvider>
  );
}

function RequireAuth({ children }) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();

  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate("/login");
    }
  }, [isLoaded, isSignedIn, navigate]);

  if (!isLoaded) return null;
  if (!isSignedIn) return null;

  return children;
}

function RequireAdmin({ children }) {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const { user, isLoaded: userLoaded } = useUser();

  useEffect(() => {
    if (!isLoaded || !userLoaded) return;
    if (!isSignedIn) {
      navigate("/login");
      return;
    }
    if (user?.publicMetadata?.role !== "admin") {
      navigate("/");
    }
  }, [isLoaded, userLoaded, isSignedIn, user, navigate]);

  if (!isLoaded || !userLoaded) return null;
  if (!isSignedIn) return null;
  if (user?.publicMetadata?.role !== "admin") return null;

  return children;
}

export default App;
